import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Avatar,
  Row,
  Col,
  Statistic,
  Image,
  Tooltip,
  Typography,
  Tabs,
  message,
  Popconfirm
} from 'antd';
import {
  UserOutlined,
  VideoCameraOutlined,
  EyeOutlined,
  HeartOutlined,
  MessageOutlined,
  ShareAltOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

const AdminOverview = () => {
  const [allAccounts, setAllAccounts] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalAccounts: 0,
    totalVideos: 0,
    totalViews: 0,
    totalLikes: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const [accountsResponse, videosResponse, usersResponse] = await Promise.all([
        axios.get('/api/admin/all-accounts', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/admin/all-videos', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/users', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const accounts = accountsResponse.data || [];
      const videosData = videosResponse.data || {};
      const videos = videosData.videos || [];
      const users = usersResponse.data || [];

      setAllAccounts(accounts);
      setAllVideos(videos);
      
      setStats({
        totalUsers: users.length,
        totalAccounts: accounts.length,
        totalVideos: videos.length,
        totalViews: videos.reduce((sum, v) => sum + (v.view_count || 0), 0),
        totalLikes: videos.reduce((sum, v) => sum + (v.like_count || 0), 0)
      });
    } catch (error) {
      message.error('Failed to load data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/admin/accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Account deleted successfully');
      loadData();
    } catch (error) {
      message.error('Failed to delete account: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteVideo = async (videoId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/admin/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Video deleted successfully');
      loadData();
    } catch (error) {
      message.error('Failed to delete video: ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadVideo = async (videoUrl) => {
    try {
      const response = await axios.get('/api/tiktok-management/video/download', {
        params: { url: videoUrl, hd: 1 },
        headers: { Authorization: `Bearer tiktok-ai-huydev-2025` }
      });

      if (response.data && response.data.data) {
        window.open(response.data.data.play, '_blank');
        message.success('Đang tải video...');
      }
    } catch (error) {
      message.error('Lỗi khi tải video: ' + (error.response?.data?.detail || error.message));
    }
  };

  const accountColumns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar src={record.user?.avatar_url} icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.user?.username || 'Unknown'}</div>
            <div style={{ fontSize: 12, color: '#666' }}>ID: {record.user?.id}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'TikTok Account',
      key: 'tiktok_account',
      render: (_, record) => (
        <Space>
          <Avatar src={record.avatar_url} icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>@{record.username}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{record.nickname}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Stats',
      key: 'stats',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div style={{ fontSize: 12 }}>
            <UserOutlined /> {record.follower_count?.toLocaleString() || 0} followers
          </div>
          <div style={{ fontSize: 12 }}>
            <VideoCameraOutlined /> {record.video_count?.toLocaleString() || 0} videos
          </div>
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString('en-US'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Delete this account?"
          onConfirm={() => handleDeleteAccount(record.id)}
          okText="Delete"
          cancelText="Cancel"
        >
          <Button danger size="small" icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const videoColumns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar src={record.account?.user?.avatar_url} icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.account?.user?.username || 'Unknown'}</div>
            <div style={{ fontSize: 12, color: '#666' }}>ID: {record.account?.user?.id}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Thumbnail',
      dataIndex: 'cover_url',
      key: 'thumbnail',
      width: 120,
      render: (url, record) => (
        <div style={{ position: 'relative' }}>
          <Image
            src={url}
            alt="Video thumbnail"
            width={80}
            height={80}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
          />
          <PlayCircleOutlined 
            style={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              fontSize: 20,
              color: 'white',
              textShadow: '0 0 4px rgba(0,0,0,0.8)'
            }} 
          />
        </div>
      ),
    },
    {
      title: 'Video Info',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            {record.title || 'No title'}
          </div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
            ID: {record.video_id}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            Author: @{record.account?.username || 'Unknown'}
          </div>
          {record.duration && (
            <div style={{ fontSize: 12, color: '#666' }}>
              Duration: {Math.floor(record.duration / 60)}:{(record.duration % 60).toString().padStart(2, '0')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Statistics',
      key: 'stats',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div style={{ fontSize: 12 }}>
            <EyeOutlined /> {record.view_count?.toLocaleString() || 0}
          </div>
          <div style={{ fontSize: 12 }}>
            <HeartOutlined /> {record.like_count?.toLocaleString() || 0}
          </div>
          <div style={{ fontSize: 12 }}>
            <MessageOutlined /> {record.comment_count?.toLocaleString() || 0}
          </div>
          <div style={{ fontSize: 12 }}>
            <ShareAltOutlined /> {record.share_count?.toLocaleString() || 0}
          </div>
        </Space>
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_checked',
      key: 'last_checked',
      width: 120,
      render: (date) => (
        <div style={{ fontSize: 12 }}>
          {date ? new Date(date).toLocaleDateString('en-US') : 'Never'}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Button 
            size="small" 
            icon={<PlayCircleOutlined />}
            onClick={() => window.open(record.video_url, '_blank')}
          >
            View
          </Button>
          <Button 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteVideo(record.id)}
            danger
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={4}>
            <Statistic
              title="Total Users"
              value={stats.totalUsers}
              prefix={<UserOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Total Accounts"
              value={stats.totalAccounts}
              prefix={<UserOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Total Videos"
              value={stats.totalVideos}
              prefix={<VideoCameraOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Views"
              value={stats.totalViews}
              prefix={<EyeOutlined />}
              formatter={(value) => value.toLocaleString()}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Likes"
              value={stats.totalLikes}
              prefix={<HeartOutlined />}
              formatter={(value) => value.toLocaleString()}
            />
          </Col>
        </Row>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>Admin Overview</Title>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Refresh
          </Button>
        </div>

        <Tabs defaultActiveKey="accounts">
          <TabPane tab="All TikTok Accounts" key="accounts">
            <Table
              columns={accountColumns}
              dataSource={allAccounts}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 800 }}
            />
          </TabPane>
          
          <TabPane tab="All Videos" key="videos">
            <Table
              columns={videoColumns}
              dataSource={allVideos}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1000 }}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default AdminOverview;